import { createContext, useContext, useState, useRef, useEffect, useCallback } from 'react';
import { normalizeCreatorMedia, spotifyEmbedUrl } from '../lib/creatorPlayback';

const AudioContext = createContext();
const API = process.env.REACT_APP_BACKEND_URL || '';
const RADIO_LISTENER_KEY = 'rastyle_radio_listener_id';
const PLAYER_PRESENTATION_KEY = 'rastyle_player_presentation_v1';
const MEDIA_VIEW_MODE_KEY = 'rastyle_media_view_mode_v1';

const readPlaybackPreference = (key, allowed, fallback) => {
  try {
    const value = window.localStorage.getItem(key);
    return allowed.includes(value) ? value : fallback;
  } catch {
    return fallback;
  }
};

const getRadioListenerId = () => {
  try {
    const existing = window.localStorage.getItem(RADIO_LISTENER_KEY);
    if (existing) return existing;
    const suffix = window.crypto?.randomUUID?.()
      || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const listenerId = `listener-${suffix}`;
    window.localStorage.setItem(RADIO_LISTENER_KEY, listenerId);
    return listenerId;
  } catch {
    return `listener-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
};

export const useAudio = () => {
  const context = useContext(AudioContext);
  if (!context) {
    throw new Error('useAudio must be used within AudioProvider');
  }
  return context;
};

// Load YouTube IFrame API once
let ytApiLoaded = false;
let ytApiPromise = null;
const loadYouTubeApi = () => {
  if (ytApiLoaded) return Promise.resolve();
  if (ytApiPromise) return ytApiPromise;
  ytApiPromise = new Promise((resolve, reject) => {
    if (window.YT && window.YT.Player) {
      ytApiLoaded = true;
      resolve();
      return;
    }
    window.onYouTubeIframeAPIReady = () => {
      ytApiLoaded = true;
      resolve();
    };
    const tag = document.createElement('script');
    tag.src = 'https://www.youtube.com/iframe_api';
    tag.onerror = () => {
      ytApiPromise = null;
      reject(new Error('YouTube player could not be loaded'));
    };
    document.head.appendChild(tag);
  });
  return ytApiPromise;
};

export const AudioProvider = ({ children }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentTrack, setCurrentTrack] = useState({
    type: 'radio',
    title: 'Rastyle Radio',
    artist: 'Live Stream',
    url: 'https://stream.zeno.fm/p6xpr1pt5x8uv',
    cover: 'https://images.pexels.com/photos/1763075/pexels-photo-1763075.jpeg'
  });
  const [volume, setVolume] = useState(0.7);
  const [showPlayer, setShowPlayer] = useState(true);
  const [useEmbedPlayer, setUseEmbedPlayer] = useState(false);
  const [embedUrl, setEmbedUrl] = useState(null);
  const [embedLoaded, setEmbedLoaded] = useState(false);
  const [activeSource, setActiveSource] = useState('audio'); // 'audio' | 'video' | 'embed' | 'youtube'
  const [playbackError, setPlaybackError] = useState('');
  const [playerPresentation, setPlayerPresentation] = useState(() => (
    readPlaybackPreference(PLAYER_PRESENTATION_KEY, ['expanded', 'minimized'], 'expanded')
  ));
  const [mediaViewMode, setMediaViewMode] = useState(() => (
    readPlaybackPreference(MEDIA_VIEW_MODE_KEY, ['video', 'audio'], 'video')
  ));
  const audioRef = useRef(null);
  const videoRef = useRef(null);
  const ytPlayerRef = useRef(null);
  const radioSessionRef = useRef(null);

  useEffect(() => {
    try { window.localStorage.setItem(PLAYER_PRESENTATION_KEY, playerPresentation); } catch {}
  }, [playerPresentation]);

  useEffect(() => {
    try { window.localStorage.setItem(MEDIA_VIEW_MODE_KEY, mediaViewMode); } catch {}
  }, [mediaViewMode]);

  const endRadioSession = useCallback(() => {
    const sessionId = radioSessionRef.current;
    if (!sessionId) return;
    radioSessionRef.current = null;
    fetch(`${API}/api/radio/listeners/stop`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ session_id: sessionId }),
      keepalive: true,
    }).catch(() => {});
  }, []);

  useEffect(() => {
    let cancelled = false;
    let heartbeatTimer;

    const begin = async () => {
      if (radioSessionRef.current) return;
      try {
        const response = await fetch(`${API}/api/radio/listeners/start`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            listener_id: getRadioListenerId(),
            source: window.location.pathname === '/radio' ? 'radio_page' : 'global_player',
            path: window.location.pathname,
            referrer: document.referrer || null,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || null,
          }),
        });
        if (!response.ok) return;
        const data = await response.json();
        if (cancelled) {
          fetch(`${API}/api/radio/listeners/stop`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ session_id: data.session_id }),
            keepalive: true,
          }).catch(() => {});
          return;
        }
        radioSessionRef.current = data.session_id;
        heartbeatTimer = window.setInterval(() => {
          const sessionId = radioSessionRef.current;
          if (!sessionId) return;
          fetch(`${API}/api/radio/listeners/heartbeat`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ session_id: sessionId }),
          }).catch(() => {});
        }, (data.heartbeat_seconds || 30) * 1000);
      } catch {
        // Playback remains available if listener analytics is offline.
      }
    };

    if (isPlaying && currentTrack.type === 'radio') begin();
    else endRadioSession();
    return () => {
      cancelled = true;
      if (heartbeatTimer) window.clearInterval(heartbeatTimer);
      if (radioSessionRef.current) endRadioSession();
    };
  }, [currentTrack.type, endRadioSession, isPlaying]);

  useEffect(() => {
    window.addEventListener('pagehide', endRadioSession);
    return () => window.removeEventListener('pagehide', endRadioSession);
  }, [endRadioSession]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
    }
    if (videoRef.current) {
      videoRef.current.volume = volume;
    }
    if (ytPlayerRef.current && typeof ytPlayerRef.current.setVolume === 'function') {
      ytPlayerRef.current.setVolume(volume * 100);
    }
  }, [volume]);

  // Handle audio element events
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const handleCanPlay = () => setIsLoading(false);
    const handleWaiting = () => setIsLoading(true);
    const handlePlaying = () => { setIsLoading(false); setIsPlaying(true); };
    const handlePause = () => { setIsPlaying(false); setIsLoading(false); };
    const handleError = (e) => { console.error('Audio error:', e); setIsLoading(false); setIsPlaying(false); };
    audio.addEventListener('canplay', handleCanPlay);
    audio.addEventListener('waiting', handleWaiting);
    audio.addEventListener('playing', handlePlaying);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('error', handleError);
    return () => {
      audio.removeEventListener('canplay', handleCanPlay);
      audio.removeEventListener('waiting', handleWaiting);
      audio.removeEventListener('playing', handlePlaying);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('error', handleError);
    };
  }, []);

  // ── Stop helpers ──────────────────────────────────

  const stopAudio = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  }, []);

  const stopEmbed = useCallback(() => {
    setUseEmbedPlayer(false);
    setEmbedUrl(null);
    setEmbedLoaded(false);
  }, []);

  const stopVideo = useCallback(() => {
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  }, []);

  const stopYouTube = useCallback(() => {
    try {
      if (ytPlayerRef.current && typeof ytPlayerRef.current.stopVideo === 'function') {
        ytPlayerRef.current.stopVideo();
      }
    } catch {}
  }, []);

  const stopAll = useCallback(() => {
    stopAudio();
    stopVideo();
    stopEmbed();
    stopYouTube();
    setIsPlaying(false);
    setIsLoading(false);
    setPlaybackError('');
  }, [stopAudio, stopEmbed, stopVideo, stopYouTube]);

  // ── Play / Pause ──────────────────────────────────

  const play = useCallback(() => {
    if (activeSource === 'youtube') {
      try {
        if (ytPlayerRef.current && typeof ytPlayerRef.current.playVideo === 'function') {
          ytPlayerRef.current.playVideo();
        }
      } catch {}
      setIsPlaying(true);
      return;
    }
    if (activeSource === 'embed') {
      setIsPlaying(true);
      return;
    }
    if (activeSource === 'video') {
      setIsLoading(true);
      videoRef.current?.play().catch(err => {
        console.error('Video playback error:', err);
        setIsLoading(false);
      });
      return;
    }
    if (audioRef.current) {
      setIsLoading(true);
      audioRef.current.play().catch(err => {
        console.error('Playback error:', err);
        setIsLoading(false);
      });
    }
  }, [activeSource]);

  const pause = useCallback(() => {
    if (activeSource === 'youtube') {
      try {
        if (ytPlayerRef.current && typeof ytPlayerRef.current.pauseVideo === 'function') {
          ytPlayerRef.current.pauseVideo();
        }
      } catch {}
      setIsPlaying(false);
      return;
    }
    if (activeSource === 'embed') {
      setIsPlaying(false);
      return;
    }
    if (activeSource === 'video') {
      videoRef.current?.pause();
      setIsPlaying(false);
      setIsLoading(false);
      return;
    }
    if (audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
      setIsLoading(false);
    }
  }, [activeSource]);

  const togglePlay = useCallback(() => {
    if (isPlaying) pause(); else play();
  }, [isPlaying, play, pause]);

  const changeTrack = useCallback((track) => {
    stopAll();
    setCurrentTrack(track);
    setActiveSource('audio');
  }, [stopAll]);

  // ── HearThis embed helpers ────────────────────────

  const getHearThisEmbedUrl = useCallback((mixtape) => {
    if (mixtape.hearthis_url) {
      const match = mixtape.hearthis_url.match(/hearthis\.at\/([^\/]+)\/([^\/]+)/);
      if (match) {
        return `https://hearthis.at/embed/${match[1]}/${match[2]}/?hcolor=d4af37&color=d4af37&style=2&block_size=2&block_space=1&background=1&waveform=1&cover=1&autoplay=0&css=`;
      }
    }
    if (mixtape.hearthis_id) {
      return `https://hearthis.at/embed/${mixtape.hearthis_id}/?hcolor=d4af37&color=d4af37&style=2&block_size=2&block_space=1&background=1&waveform=1&cover=1&autoplay=0&css=`;
    }
    return null;
  }, []);

  const _fallbackToEmbed = useCallback((mixtape) => {
    const embedPlayerUrl = getHearThisEmbedUrl(mixtape);
    if (embedPlayerUrl) {
      stopYouTube();
      setActiveSource('embed');
      setUseEmbedPlayer(true);
      setEmbedUrl(embedPlayerUrl);
      setEmbedLoaded(false);
      setCurrentTrack(prev => ({ ...prev, useEmbed: true }));
      setIsPlaying(true);
      setTimeout(() => setIsLoading(false), 2000);
    } else {
      setIsLoading(false);
    }
  }, [getHearThisEmbedUrl, stopYouTube]);

  // ── Play Mixtape ──────────────────────────────────

  const playMixtape = useCallback((mixtape, preferEmbed = true) => {
    // Stop everything first
    stopAll();
    setActiveSource('audio');
    setIsLoading(true);
    setShowPlayer(true);

    setCurrentTrack({
      ...mixtape,
      type: 'mixtape',
      title: mixtape.title,
      artist: mixtape.dj,
      cover: mixtape.cover || 'https://images.pexels.com/photos/1763075/pexels-photo-1763075.jpeg',
      useEmbed: false
    });

    // PlayBlock resolved URL
    if (mixtape.resolved_audio_url || mixtape.playblock) {
      const directUrl = mixtape.resolved_audio_url;
      if (directUrl) {
        if (audioRef.current) {
          audioRef.current.src = directUrl;
          audioRef.current.load();
          audioRef.current.play().then(() => {
            setIsPlaying(true);
            setIsLoading(false);
          }).catch(() => _fallbackToEmbed(mixtape));
        }
        return;
      }
    }

    // HearThis — try PlayBlock first
    if (mixtape.hearthis_url || mixtape.synced_from_hearthis) {
      const API = process.env.REACT_APP_BACKEND_URL;
      fetch(`${API}/api/playblock/resolve/${mixtape.id || ''}`)
        .then(r => r.ok ? r.json() : null)
        .then(data => {
          if (data?.audio_url && !data.is_preview) {
            if (audioRef.current) {
              audioRef.current.src = data.audio_url;
              audioRef.current.load();
              audioRef.current.play().then(() => {
                setIsPlaying(true);
                setIsLoading(false);
              }).catch(() => _fallbackToEmbed(mixtape));
            }
          } else {
            _fallbackToEmbed(mixtape);
          }
        })
        .catch(() => _fallbackToEmbed(mixtape));
      return;
    }

    // Direct audio URL
    const directUrl = mixtape.audio_url || mixtape.stream_url;
    if (directUrl) {
      if (audioRef.current) {
        audioRef.current.src = directUrl;
        audioRef.current.load();
        audioRef.current.play().then(() => {
          setIsPlaying(true);
          setIsLoading(false);
        }).catch(() => {
          setIsLoading(false);
          setPlaybackError('This mix could not start inside Rastyle. Please try again.');
        });
      }
      return;
    }

    setIsLoading(false);
    setPlaybackError('This mix is not available for playback yet.');
  }, [stopAll, _fallbackToEmbed]);

  // ── Play Radio ────────────────────────────────────

  const playRadio = useCallback(() => {
    stopAll();
    setActiveSource('audio');
    setIsLoading(true);
    setCurrentTrack({
      type: 'radio',
      title: 'Rastyle Radio',
      artist: 'Live Stream',
      url: 'https://stream.zeno.fm/p6xpr1pt5x8uv',
      cover: 'https://images.pexels.com/photos/1763075/pexels-photo-1763075.jpeg'
    });

    setTimeout(() => {
      if (audioRef.current) {
        audioRef.current.src = 'https://stream.zeno.fm/p6xpr1pt5x8uv';
        audioRef.current.load();
        audioRef.current.play().catch(err => {
          console.error('Radio playback error:', err);
          setIsLoading(false);
        });
      }
    }, 100);
  }, [stopAll]);

  // ── Play YouTube ──────────────────────────────────

  const _createYouTubePlayer = useCallback((videoId) => {
    const container = document.getElementById('yt-playblock-player');
    if (!container) {
      setPlaybackError('The video player is not ready yet. Tap play to retry.');
      setIsLoading(false);
      return false;
    }

    try {
      ytPlayerRef.current?.destroy?.();
    } catch {}
    ytPlayerRef.current = null;

    try {
      ytPlayerRef.current = new window.YT.Player('yt-playblock-player', {
        height: '100%',
        width: '100%',
        videoId: videoId,
        playerVars: {
          autoplay: 1,
          controls: 1,
          modestbranding: 1,
          playsinline: 1,
          rel: 0,
          origin: window.location.origin,
        },
        events: {
          onReady: (event) => {
            setPlaybackError('');
            event.target.setVolume(volume * 100);
            event.target.playVideo();
          },
          onStateChange: (event) => {
            const state = event.data;
            if (state === window.YT.PlayerState.PLAYING) {
              setIsPlaying(true);
              setIsLoading(false);
            } else if (state === window.YT.PlayerState.PAUSED) {
              setIsPlaying(false);
            } else if (state === window.YT.PlayerState.ENDED) {
              setIsPlaying(false);
              // Dispatch custom event so AudioPlayer/QueueContext can advance
              window.dispatchEvent(new CustomEvent('youtube-track-ended'));
            } else if (state === window.YT.PlayerState.BUFFERING) {
              setIsLoading(true);
            }
          },
          onError: (event) => {
            console.error('YouTube player error:', event.data);
            setPlaybackError('This YouTube video is unavailable for embedded playback.');
            setIsLoading(false);
            setIsPlaying(false);
          },
        },
      });
      return true;
    } catch (err) {
      console.error('Failed to create YT player:', err);
      setPlaybackError('The YouTube player could not start. Tap play to retry.');
      setIsLoading(false);
      return false;
    }
  }, [volume]);

  const playYouTube = useCallback(async (video) => {
    stopAll();
    setActiveSource('youtube');
    setIsLoading(true);
    setPlaybackError('');
    setShowPlayer(true);

    setCurrentTrack({
      ...video,
      type: 'youtube',
      title: video.title,
      artist: video.channel_title || video.artist || 'YouTube',
      cover: video.thumbnail || video.cover || 'https://images.pexels.com/photos/1763075/pexels-photo-1763075.jpeg',
      externalUrl: video.url || `https://www.youtube.com/watch?v=${video.youtube_id || video.video_id}`,
      youtubeId: video.youtube_id || video.video_id,
      videoType: video.type || 'record',
    });

    try {
      await loadYouTubeApi();
    } catch {
      setIsLoading(false);
      setPlaybackError('YouTube could not be reached. Check your connection and tap play to retry.');
      return;
    }

    const videoId = video.youtube_id || video.video_id;
    if (!videoId) {
      setIsLoading(false);
      setPlaybackError('This video is missing its YouTube ID.');
      return;
    }

    // If player already exists, just load new video
    const existingFrame = ytPlayerRef.current?.getIframe?.();
    if (existingFrame?.isConnected && typeof ytPlayerRef.current.loadVideoById === 'function') {
      try {
        ytPlayerRef.current.loadVideoById(videoId);
        ytPlayerRef.current.setVolume(volume * 100);
      } catch {
        _createYouTubePlayer(videoId);
      }
      return;
    }

    // Let React mount the visible universal-player surface before the API
    // replaces it with an iframe. This prevents the former audio-only player.
    for (let attempt = 0; attempt < 12; attempt += 1) {
      if (document.getElementById('yt-playblock-player')) break;
      await new Promise((resolve) => {
        const schedule = window.requestAnimationFrame || ((callback) => window.setTimeout(callback, 16));
        schedule(resolve);
      });
    }
    _createYouTubePlayer(videoId);
  }, [_createYouTubePlayer, stopAll, volume]);

  const playStream = useCallback((track) => {
    const sourceUrl = track.stream_url || track.audio_url || track.resolved_audio_url || track.url;
    if (!sourceUrl) return false;

    stopAll();
    setActiveSource('audio');
    setIsLoading(true);
    setShowPlayer(true);
    setCurrentTrack({
      ...track,
      type: track.track_type === 'radio' || track.type === 'creator-radio' ? 'creator-radio' : 'audio',
      title: track.title || 'Creator audio',
      artist: track.artist || track.dj || 'Rastyle creator',
      cover: track.cover || track.cover_image || 'https://images.pexels.com/photos/1763075/pexels-photo-1763075.jpeg',
      url: sourceUrl,
      externalUrl: track.externalUrl || track.external_url || sourceUrl,
    });
    window.setTimeout(() => {
      if (!audioRef.current) return;
      audioRef.current.src = sourceUrl;
      audioRef.current.load();
      audioRef.current.play().then(() => {
        setIsPlaying(true);
        setIsLoading(false);
      }).catch((error) => {
        console.error('Creator audio playback error:', error);
        setIsPlaying(false);
        setIsLoading(false);
      });
    }, 0);
    return true;
  }, [stopAll]);

  const playVideo = useCallback((track) => {
    const sourceUrl = track.video_url || track.url;
    if (!sourceUrl) return false;

    stopAll();
    setActiveSource('video');
    setIsLoading(true);
    setShowPlayer(true);
    setCurrentTrack({
      ...track,
      type: 'video',
      title: track.title || 'Creator video',
      artist: track.artist || 'Rastyle creator',
      cover: track.cover || track.thumbnail || track.thumbnail_url || 'https://images.pexels.com/photos/1763075/pexels-photo-1763075.jpeg',
      url: sourceUrl,
      externalUrl: track.externalUrl || track.external_url || sourceUrl,
    });
    window.setTimeout(() => {
      if (!videoRef.current) return;
      videoRef.current.src = sourceUrl;
      videoRef.current.volume = volume;
      videoRef.current.load();
      videoRef.current.play().then(() => {
        setIsPlaying(true);
        setIsLoading(false);
      }).catch((error) => {
        console.error('Creator video playback error:', error);
        setIsPlaying(false);
        setIsLoading(false);
      });
    }, 0);
    return true;
  }, [stopAll, volume]);

  const playEmbed = useCallback((track) => {
    const sourceUrl = track.embed_url || track.embedUrl;
    if (!sourceUrl) return false;

    stopAll();
    setActiveSource('embed');
    setUseEmbedPlayer(true);
    setEmbedUrl(sourceUrl);
    setEmbedLoaded(false);
    setShowPlayer(true);
    setCurrentTrack({
      ...track,
      type: 'embed',
      title: track.title || 'Creator media',
      artist: track.artist || 'Rastyle creator',
      cover: track.cover || track.cover_image || 'https://images.pexels.com/photos/1763075/pexels-photo-1763075.jpeg',
      externalUrl: track.externalUrl || track.external_url || track.url || '',
      useEmbed: true,
    });
    setIsPlaying(true);
    setIsLoading(true);
    return true;
  }, [stopAll]);

  const playSpotifyRelease = useCallback((release) => playEmbed({
    ...release,
    track_type: 'embed',
    provider: 'spotify',
    embed_url: release.embed_url || spotifyEmbedUrl(release.spotify_url, release.spotify_id, 'album'),
    externalUrl: release.spotify_url,
  }), [playEmbed]);

  const playContent = useCallback((track) => {
    if (!track) return false;
    const media = normalizeCreatorMedia(track) || track;
    const trackType = media.track_type || media.type;
    if (trackType === 'youtube' || media.youtube_id || media.video_id) {
      playYouTube({
        ...media,
        youtube_id: media.youtube_id || media.video_id || media.track_id,
        channel_title: media.channel_title || media.artist,
        thumbnail: media.thumbnail || media.cover,
        type: media.video_type || 'creator',
      });
      return true;
    }
    if (trackType === 'embed' || media.embed_url || media.embedUrl) return playEmbed(media);
    if (trackType === 'video' || media.video_url) return playVideo(media);
    if (trackType === 'mixtape' || media.synced_from_hearthis || media.hearthis_url) {
      playMixtape({
        ...media,
        id: media.id || media.track_id,
        dj: media.dj || media.artist,
        cover: media.cover || media.cover_image,
      }, Boolean(media.hearthis_url || media.synced_from_hearthis));
      return true;
    }
    if (trackType === 'audio' || trackType === 'radio' || media.stream_url || media.audio_url || media.resolved_audio_url) {
      return playStream(media);
    }
    return false;
  }, [playEmbed, playMixtape, playStream, playVideo, playYouTube]);

  // Handle embed loaded
  const onEmbedLoaded = useCallback(() => {
    setEmbedLoaded(true);
    setIsLoading(false);
  }, []);

  const onMediaPlaying = useCallback(() => {
    setIsLoading(false);
    setIsPlaying(true);
  }, []);

  const onMediaPause = useCallback(() => {
    setIsLoading(false);
    setIsPlaying(false);
  }, []);

  const onMediaWaiting = useCallback(() => setIsLoading(true), []);

  const value = {
    isPlaying,
    isLoading,
    currentTrack,
    volume,
    showPlayer,
    useEmbedPlayer,
    embedUrl,
    embedLoaded,
    activeSource,
    playbackError,
    playerPresentation,
    mediaViewMode,
    audioRef,
    videoRef,
    ytPlayerRef,
    play,
    pause,
    togglePlay,
    changeTrack,
    playMixtape,
    playRadio,
    playYouTube,
    playStream,
    playVideo,
    playEmbed,
    playContent,
    playSpotifyRelease,
    stopAll,
    setVolume,
    setShowPlayer,
    setUseEmbedPlayer,
    setPlayerPresentation,
    setMediaViewMode,
    onEmbedLoaded,
    onMediaPlaying,
    onMediaPause,
    onMediaWaiting,
  };

  return (
    <AudioContext.Provider value={value}>
      {children}
    </AudioContext.Provider>
  );
};

export default AudioContext;
